import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Sparkles, ArrowUpRight, Eye, Share2, Twitter, Linkedin, Facebook } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import SocialMeta from "@/components/SocialMeta";
import Logo from "@/components/Logo";

export default function ShareLogo() {
  const { slug } = useParams();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/share/logo/${slug}`)
      .then((r) => setShare(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const shareUrl = window.location.href;
  const tweetText = encodeURIComponent(
    `Just generated my brand logo with @MakeYourBrandAI 🔥 Check it out:`
  );

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied — share away.");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A] grid place-items-center">
        <div className="text-white/40 font-mono text-sm">Loading…</div>
      </div>
    );
  }

  if (notFound || !share) {
    return (
      <div className="min-h-screen bg-[#05050A] text-white grid place-items-center p-6">
        <div className="glass rounded-2xl p-10 text-center max-w-md">
          <h1 className="font-heading text-3xl font-black">Share not found</h1>
          <p className="text-white/50 mt-3">This logo share may have been removed.</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 brand-gradient px-5 py-3 rounded-lg text-sm font-semibold"
          >
            Build your own <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  const imgSrc = share.storage_path
    ? `${API}/public/files/${share.storage_path}`
    : `data:image/png;base64,${share.image_b64}`;

  return (
    <div className="min-h-screen bg-[#05050A] text-white grid-bg" data-testid="share-logo-root">
      <SocialMeta
        title={`${share.business_name || "AI logo"} — logo generated by AI`}
        description={`Tagline: "${share.tagline || ""}" — generated free in 10 seconds at MakeYourBrand.IN.`}
        image={`${API}/og/share/${share.logo_id || share.id}`}
        url={shareUrl}
      />
      {/* Topbar */}
      <header className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size="h-9" />
          </Link>
          <Link
            to="/register"
            data-testid="share-cta-topbar"
            className="brand-gradient px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90"
          >
            Try it free
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Logo card */}
          <div className="lg:col-span-7">
            <div className="relative">
              <div
                className="absolute -inset-10 opacity-30 blur-3xl rounded-full"
                style={{
                  background: `radial-gradient(circle, ${share.brand_color}, transparent 70%)`,
                }}
              />
              <div className="relative glass rounded-3xl p-8 lg:p-14">
                <img
                  src={imgSrc}
                  alt={`${share.business_name} logo`}
                  className="w-full max-w-md mx-auto"
                  data-testid="share-logo-image"
                />
              </div>
            </div>
            <div className="mt-6 flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-white/40 font-mono">
              <span className="flex items-center gap-1.5">
                <Eye className="h-3 w-3" /> {share.views} views
              </span>
              <span>·</span>
              <span>Generated by AI</span>
            </div>
          </div>

          {/* Pitch */}
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-4">
              {share.industry || "A new brand"}
            </div>
            <h1 className="font-heading text-4xl lg:text-6xl font-black tracking-tighter leading-[0.95]" data-testid="share-business-name">
              {share.business_name}
            </h1>
            {share.tagline && (
              <p className="mt-6 text-lg text-white/70 leading-relaxed">{share.tagline}</p>
            )}

            <div className="mt-10 glass rounded-2xl p-6">
              <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-2">Want one like this?</div>
              <h3 className="font-heading text-xl font-bold">Build your entire brand with AI</h3>
              <p className="text-sm text-white/60 mt-2 leading-relaxed">
                Logos, websites, content, ads — generated in minutes by MakeYourBrand.IN.
              </p>
              <Link
                to="/register"
                data-testid="share-cta-primary"
                className="mt-5 inline-flex items-center gap-2 brand-gradient px-5 py-3 rounded-lg font-semibold text-sm hover:opacity-90 glow-orange"
              >
                Build my brand free <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Share</span>
              <a
                href={`https://twitter.com/intent/tweet?text=${tweetText}&url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="share-twitter"
                className="glass p-2.5 rounded-lg hover:bg-white/5"
              >
                <Twitter className="h-4 w-4" />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="share-linkedin"
                className="glass p-2.5 rounded-lg hover:bg-white/5"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="share-facebook"
                className="glass p-2.5 rounded-lg hover:bg-white/5"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <button
                onClick={copyLink}
                data-testid="share-copy-link"
                className="glass px-3 py-2 rounded-lg hover:bg-white/5 text-xs inline-flex items-center gap-2"
              >
                <Share2 className="h-3.5 w-3.5" /> Copy link
              </button>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-6 text-xs text-white/40 font-mono flex justify-between">
          <span>© 2026 SURAJIT MAJHI HUF · makeyourbrand.in</span>
          <Link to="/" className="hover:text-white">Home →</Link>
        </div>
      </footer>
    </div>
  );
}
